import TemplateEditor from '@/components/main/editor/TemplateEditor'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import MarkdownRenderer from '@/modules/admin/wizard-forms-builder/unified/ui-components/MarkdownRenderer'
import DOMPurify from 'dompurify'
import { FileText, Folder, X } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { FinderItem } from '../types'
import { formatBytes, formatFullDate, getItemIcon, getOwnerName } from '../utils'

interface FinderQuickLookProps {
    item: FinderItem | null
    onClose: () => void
    onNavigateToItem?: (item: FinderItem) => void
}

export function FinderQuickLook({ item, onClose, onNavigateToItem }: FinderQuickLookProps) {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Escape') {
                e.preventDefault()
                onClose()
            }
        }

        if (item) {
            document.addEventListener('keydown', handleKeyDown)
            return () => document.removeEventListener('keydown', handleKeyDown)
        }
    }, [item, onClose])

    const contentPreview = useMemo(() => {
        if (!item) return null

        // For folders, show child items
        if (item.type === 'folder') {
            const children = item.items || []
            if (children.length === 0) {
                return (
                    <div className='flex flex-col items-center justify-center h-full text-center py-12'>
                        <Folder className='w-16 h-16 text-gray-200 mb-4' />
                        <p className='text-gray-400'>This folder is empty</p>
                    </div>
                )
            }
            return (
                <div className='p-4'>
                    <p className='text-sm text-gray-500 mb-3'>
                        {children.length} item{children.length !== 1 ? 's' : ''} in this folder
                    </p>
                    <div className='space-y-1 max-h-[350px] overflow-y-auto'>
                        {children.slice(0, 30).map((child: any) => (
                            <div
                                key={child.id}
                                className='flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors'
                                onClick={() => {
                                    onNavigateToItem?.(child)
                                    onClose()
                                }}
                            >
                                <div className='flex items-center gap-2 min-w-0'>
                                    {child.isFolder ? (
                                        <Folder className='w-4 h-4 text-amber-500 shrink-0' />
                                    ) : (
                                        <FileText className='w-4 h-4 text-blue-500 shrink-0' />
                                    )}
                                    <span className='text-sm text-gray-700 truncate'>{child.name}</span>
                                </div>
                                <span
                                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                                        child.isFolder ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                                    }`}
                                >
                                    {child.isFolder ? 'Folder' : 'Document'}
                                </span>
                            </div>
                        ))}
                        {children.length > 30 && (
                            <p className='text-sm text-gray-400 px-3 py-2'>+{children.length - 30} more...</p>
                        )}
                    </div>
                </div>
            )
        }

        // For images
        if (item.data_type === 'image' && item.url) {
            return (
                <div className='flex items-center justify-center p-4'>
                    <img
                        src={item.url}
                        alt={item.name}
                        className='max-w-full max-h-full object-contain rounded-lg shadow-lg'
                    />
                </div>
            )
        }

        // For videos
        if (item.data_type === 'video' && item.url) {
            return (
                <div className='flex items-center justify-center p-4'>
                    <video src={item.url} controls className='w-full max-h-full rounded-lg' />
                </div>
            )
        }

        // For PDFs
        if (item.data_type === 'pdf' && item.url) {
            return <iframe src={item.url} title={`${item.name} preview`} className='w-full h-full rounded-lg' />
        }

        // For JSON content (rich text editor content)
        if (item.json && Object.keys(item.json).length > 0) {
            return (
                <div className='p-4 prose prose-sm max-w-none overflow-y-auto'>
                    <TemplateEditor
                        key={item.id || JSON.stringify(item.json)}
                        initialValue={item.json}
                        disabled={true}
                        onChange={() => {}}
                        placeholder='Preview content...'
                    />
                </div>
            )
        }

        // For HTML content
        if (item.html && item.html.trim()) {
            const sanitizedHtml = DOMPurify.sanitize(item.html, {
                ALLOWED_TAGS: [
                    'p',
                    'strong',
                    'em',
                    'ul',
                    'ol',
                    'li',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'a',
                    'img',
                    'br',
                    'blockquote',
                    'code',
                    'pre',
                ],
                ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
            })
            return (
                <div
                    className='p-4 prose prose-sm max-w-none overflow-y-auto'
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
            )
        }

        // For text/markdown content
        if (item.content && typeof item.content === 'string' && item.content.trim()) {
            return (
                <div className='p-4 overflow-y-auto'>
                    <MarkdownRenderer className='prose prose-sm max-w-none'>
                        {item.content.slice(0, 10000)}
                    </MarkdownRenderer>
                </div>
            )
        }

        // No preview available
        const icon = getItemIcon(item)
        return (
            <div className='flex flex-col items-center justify-center h-full text-center py-12'>
                <img src={icon} alt='' className='w-24 h-24 opacity-40 mb-4' />
                <p className='text-gray-400'>No preview available</p>
                <p className='text-sm text-gray-300 mt-1'>Open the file to view its contents</p>
            </div>
        )
    }, [item, onClose, onNavigateToItem])

    if (!item) return null

    const icon = getItemIcon(item)

    return (
        <Dialog open={!!item} onOpenChange={open => !open && onClose()}>
            <DialogContent className='max-w-3xl p-0 overflow-hidden bg-white border-0 shadow-2xl rounded-2xl [&>button]:hidden max-h-[85vh] flex flex-col'>
                <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0'>
                    <div className='flex items-center gap-3 min-w-0'>
                        <img src={icon} alt='' className='w-8 h-8 shrink-0' />
                        <div className='min-w-0'>
                            <h2 className='text-base font-medium text-gray-900 truncate'>{item.name}</h2>
                            <p className='text-xs text-gray-400'>
                                {item.type === 'folder'
                                    ? `${item.itemCount || item.items?.length || 0} items`
                                    : formatBytes(item.size)}
                                {item.status && ` • ${item.status}`}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className='p-2 rounded-lg hover:bg-gray-100 transition-colors shrink-0'>
                        <X className='w-5 h-5 text-gray-400' />
                    </button>
                </div>

                <div className='flex-1 min-h-[300px] bg-gray-50 overflow-auto'>{contentPreview}</div>

                <div className='px-5 py-4 border-t border-gray-100 bg-white shrink-0'>
                    <div className='grid grid-cols-4 gap-4 text-sm'>
                        <div>
                            <p className='text-[10px] text-gray-400 uppercase tracking-wide mb-0.5'>Type</p>
                            <p className='text-gray-700'>
                                {item.type === 'folder' ? 'Folder' : item.extension || 'Document'}
                            </p>
                        </div>
                        <div>
                            <p className='text-[10px] text-gray-400 uppercase tracking-wide mb-0.5'>Owner</p>
                            <p className='text-gray-700 truncate'>{getOwnerName(item)}</p>
                        </div>
                        <div>
                            <p className='text-[10px] text-gray-400 uppercase tracking-wide mb-0.5'>Modified</p>
                            <p className='text-gray-700'>{formatFullDate(item.updatedAt)}</p>
                        </div>
                        <div>
                            <p className='text-[10px] text-gray-400 uppercase tracking-wide mb-0.5'>Size</p>
                            <p className='text-gray-700'>{formatBytes(item.size)}</p>
                        </div>
                    </div>
                    <p className='text-[10px] text-gray-400 mt-3 text-center'>
                        Press <kbd className='px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono'>Space</kbd> or{' '}
                        <kbd className='px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono'>Esc</kbd> to close
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    )
}
